import React, { useState } from 'react';
import styled from '@emotion/styled';

const SelectorContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const HoverTrigger = styled.div`
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

const DocumentList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 250px;
  max-width: 400px;
  max-height: 300px;
  overflow-y: auto;
`;

const DocumentItem = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f3f4;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const FileIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: white;
  background-color: ${props => {
    switch (props.fileType) {
      case 'pdf': return '#e74c3c';
      case 'excel': return '#27ae60';
      case 'csv': return '#f39c12';
      case 'text': return '#3498db';
      case 'json': return '#9b59b6';
      case 'xml': return '#34495e';
      default: return '#95a5a6';
    }
  }};
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-size: 0.8rem;
  color: #7f8c8d;
  margin-top: 0.25rem;
`;

const PreviewButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #2980b9;
  }
`;

const EmptyMessage = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: #6c757d;
  font-style: italic;
`;

const LoadingMessage = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: #6c757d;
`;

function DocumentSelector({ 
  documents = [], 
  onPreview, 
  loading = false,
  triggerText = "Available Documents",
  triggerIcon = "📁"
}) {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handlePreview = (document) => {
    onPreview(document);
    setIsOpen(false);
  };

  const getFileType = (fileName) => {
    if (!fileName) return 'other';
    
    const extension = fileName.split('.').pop().toLowerCase();
    
    // Special handling for S3 files with misleading extensions
    if (fileName.includes('_keyvalues.json')) {
      // Keyvalues files are actually CSV data, not JSON
      return 'csv';
    }
    if (fileName.includes('_textract.json')) {
      // Textract files are JSON responses from AWS Textract
      return 'json';
    }
    
    // Check for S3 path patterns
    if (fileName.includes('/source/') && extension === 'pdf') {
      return 'pdf';
    }
    if (fileName.includes('/textract/')) {
      return 'json';
    }
    if (fileName.includes('/keyvalues/')) {
      return 'csv';
    }
    
    // Standard file type detection
    if (extension === 'pdf') return 'pdf';
    if (['xlsx', 'xls'].includes(extension)) return 'excel';
    if (extension === 'csv') return 'csv';
    if (extension === 'txt') return 'text';
    if (extension === 'json') return 'json';
    if (extension === 'xml') return 'xml';
    
    return 'other';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    if (i === 0) return `${bytes} ${sizes[i]}`;
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return 'FILE';
    
    // Special handling for S3 files with misleading extensions
    if (fileName.includes('_keyvalues.json')) {
      // Keyvalues files are actually CSV data
      return 'CSV';
    }
    if (fileName.includes('_textract.json')) {
      // Textract files are JSON responses from AWS Textract
      return 'JSON';
    }
    
    // Check for S3 path patterns
    if (fileName.includes('/source/') && fileName.endsWith('.pdf')) {
      return 'PDF';
    }
    if (fileName.includes('/textract/')) {
      return 'JSON';
    }
    if (fileName.includes('/keyvalues/')) {
      return 'CSV';
    }
    
    const extension = fileName.split('.').pop().toLowerCase();
    
    switch (extension) {
      case 'pdf': return 'PDF';
      case 'xlsx':
      case 'xls': return 'XLS';
      case 'csv': return 'CSV';
      case 'txt': return 'TXT';
      case 'json': return 'JSON';
      case 'xml': return 'XML';
      default: return extension.toUpperCase().substring(0, 4); // Show first 4 chars of extension
    }
  };

  
  return (
    <SelectorContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <HoverTrigger>
        <span>{triggerIcon}</span> {triggerText}
        {documents.length > 0 && (
          <span style={{ 
            marginLeft: '0.5rem', 
            background: '#3498db', 
            color: 'white', 
            borderRadius: '50%', 
            padding: '0.2rem 0.5rem', 
            fontSize: '0.8rem' 
          }}>
            {documents.length}
          </span>
        )}
      </HoverTrigger>
      
      {isOpen && (
        <DocumentList>
          {loading ? (
            <LoadingMessage>
              Loading documents...
            </LoadingMessage>
          ) : documents.length === 0 ? (
            <EmptyMessage>
              No documents available
            </EmptyMessage>
          ) : (
            documents.map((doc, index) => {
              // Use S3 key for more accurate file type detection, fallback to fileName
              const s3Key = doc.s3Key || '';
              const fileName = doc.name || doc.fileName || `Document ${index + 1}`;
              const fileType = getFileType(s3Key || fileName);
              const fileSize = doc.size || doc.fileSize;
              
              return (
                <DocumentItem 
                  key={index} 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePreview(doc);
                  }}
                >
                  <FileIcon fileType={fileType}>
                    {getFileIcon(s3Key || fileName)}
                  </FileIcon>
                  
                  <FileInfo>
                    <FileName>
                      {doc.name || doc.fileName || `Document ${index + 1}`}
                    </FileName>
                    {fileSize && (
                      <FileSize>
                        {formatFileSize(fileSize)}
                      </FileSize>
                    )}
                  </FileInfo>
                  
                  <PreviewButton>
                    Preview
                  </PreviewButton>
                </DocumentItem>
              );
            })
          )}
        </DocumentList>
      )}
    </SelectorContainer>
  );
}

export default DocumentSelector;
